import { EventItem } from "@/src/models/event.model";
import { useTheme } from "@/src/styles/theme-provider";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react-nativejs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { Calendar, DateData } from "react-native-calendars";

type EventsCalendarProps = Readonly<{
  events: EventItem[];
}>;

const toDateString = (date: Date): string => date.toISOString().split("T")[0];

const buildCalendarTheme = (monthTextColor: string, textSectionTitleColor: string) => ({
  monthTextColor,
  textSectionTitleColor,
  calendarBackground: "transparent",
});

const EventsCalendar = ({ events }: EventsCalendarProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  const calendarTheme = useMemo(
    () =>
      buildCalendarTheme(
        themeVars["--color-onyx-default"] as string,
        themeVars["--color-ash-1"] as string,
      ),
    [themeVars],
  );

  const today = toDateString(new Date());

  const markedDates = useMemo(() => {
    const marks: Record<string, { marked: boolean }> = {};

    events.forEach((event) => {
      const start = new Date(event?.startTime);
      const end = new Date(event?.endTime);
      let current = new Date(start);

      while (current <= end) {
        marks[toDateString(current)] = { marked: true };
        current.setDate(current.getDate() + 1);
      }
    });

    return marks;
  }, [events]);
  const renderArrow = (direction: "left" | "right") => (
    <View className="w-7 h-7 rounded-full items-center justify-center hover:bg-ash">
      {direction === "left" ? (
        <ArrowLeft2 size={16} color={themeVars["--color-primary-default"] as string} />
      ) : (
        <ArrowRight2 size={16} color={themeVars["--color-primary-default"] as string} />
      )}
    </View>
  );
  const renderHeader = (date: any) => {
    const parsed = new Date(date?.toString());
    const monthLabel = parsed.toLocaleString("default", {
      month: "short",
      year: "numeric",
    });
    return (
      <View className="flex-1 items-center justify-center">
        <Text
          className="text-sm font-medium"
          style={{ color: themeVars["--color-onyx-default"] as string }}
        >
          {monthLabel}
        </Text>
      </View>
    );
  };

  const renderDay = ({
    date,
    marking,
    state,
  }: {
    date?: DateData;
    marking?: any;
    state?: string;
  }) => {
    const isToday = date?.dateString === today;
    const isDisabled = state === "disabled";
    const hasEvent = marking?.marked ?? false;

    const dayTextColor = isToday
      ? "#FFFFFF"
      : isDisabled
        ? themeVars["--color-ash-default"] as string
        : themeVars["--color-onyx-default"] as string;

    return (
      <View className="items-center justify-center py-0.5">
        <View
          className="w-6 h-6 rounded-full items-center justify-center"
          style={isToday ? { backgroundColor: themeVars["--color-primary-default"] as string } : undefined}
        >
          <Text className="text-xs font-medium" style={{ color: dayTextColor }}>
            {date?.day}
          </Text>
        </View>
        <View className="h-2 items-center justify-center">
          {hasEvent && (
            <View
              className="w-1 h-1 rounded-full"
              style={{ backgroundColor: themeVars["--color-jade-default"] as string }}
            />
          )}
        </View>
      </View>
    );
  };

  return (
    <View className="rounded-2xl border border-ash-3 bg-chalk p-4">
      <Text className="text-sm font-bold text-onyx mb-3">
        {t("LABEL_CALENDAR")}
      </Text>

      <Calendar
        markingType="custom"
        markedDates={markedDates}
        theme={calendarTheme}
        dayComponent={renderDay}
        renderHeader={renderHeader}
        renderArrow={renderArrow}
      />
    </View>
  );
};

export default EventsCalendar;
